import React, { useState } from 'react';
import { CheckCircle2, Circle, XCircle, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useTranslation } from 'react-i18next';

// Parse date string as local date to avoid UTC timezone offset issues
const parseLocalDate = (dateString: string): Date => {
  if (!dateString) return new Date();
  // If it's a full ISO string (with T), use parseISO
  if (dateString.includes('T')) {
    return parseISO(dateString);
  }
  // If it's just a date (YYYY-MM-DD), create local date to avoid UTC offset
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

interface DealStageProgressProps {
  currentStage: string;
  onStageChange: (stage: string) => void;
  createdDate?: string;
  closedDate?: string;
  expectedCloseDate?: string;
  startDate?: string;
  onStartDateChange?: (date: Date) => void;
  onClosingDateChange?: (date: Date) => void;
  canChangeStage?: boolean;
  // Callbacks for stage transition modals
  onRequestLostReason?: () => void;
  onRequestPostMeeting?: () => void;
  onRequestQualification?: (missingFields: string[]) => void;
}

export function DealStageProgress({
  currentStage,
  onStageChange,
  createdDate,
  closedDate,
  expectedCloseDate,
  startDate,
  onStartDateChange,
  onClosingDateChange,
  canChangeStage = true,
  onRequestLostReason,
  onRequestPostMeeting,
  onRequestQualification,
}: DealStageProgressProps) {
  const { t } = useTranslation();
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [closingDateOpen, setClosingDateOpen] = useState(false);

  const STAGES = [
    { value: 'new', labelKey: 'stages.new', color: 'slate' },
    { value: 'contact', labelKey: 'stages.contact', color: 'blue' },
    { value: 'scheduled', labelKey: 'stages.scheduled', color: 'purple' },
    { value: 'proposal', labelKey: 'stages.proposal', color: 'amber' },
    { value: 'negotiation', labelKey: 'stages.negotiation', color: 'orange' },
    { value: 'won', labelKey: 'stages.won', color: 'emerald' },
  ];

  const currentIndex = STAGES.findIndex(s => s.value === currentStage);
  const isClosedLost = currentStage === 'lost';

  const displayStartDate = startDate || createdDate;
  const displayClosingDate = closedDate || expectedCloseDate;

  const getStageStatus = (index: number) => {
    if (isClosedLost) return 'future';
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'current';
    return 'future';
  };

  // Handle stage click with transition validations
  const handleStageClick = (newStage: string) => {
    if (!canChangeStage) return;

    // Lost - always request reason via callback if available
    if (newStage === 'lost') {
      if (onRequestLostReason) {
        onRequestLostReason();
      } else {
        onStageChange(newStage);
      }
      return;
    }

    // scheduled -> proposal - request post-meeting data
    if (currentStage === 'scheduled' && newStage === 'proposal') {
      if (onRequestPostMeeting) {
        onRequestPostMeeting();
      } else {
        onStageChange(newStage);
      }
      return;
    }

    // contact -> scheduled - request qualification if callback provided
    if (currentStage === 'contact' && newStage === 'scheduled') {
      if (onRequestQualification) {
        // Parent will handle validation and provide missing fields
        onRequestQualification([]);
      } else {
        onStageChange(newStage);
      }
      return;
    }

    // For all other transitions, call directly
    onStageChange(newStage);
  };

  const getStageClasses = (status: string, stageValue: string) => {
    if (status === 'completed') {
      return 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600';
    }
    if (status === 'current') {
      const colorMap: Record<string, string> = {
        new: 'bg-slate-500 text-white border-slate-600',
        contact: 'bg-blue-500 text-white border-blue-600',
        scheduled: 'bg-purple-500 text-white border-purple-600',
        proposal: 'bg-amber-500 text-white border-amber-600',
        negotiation: 'bg-orange-500 text-white border-orange-600',
        won: 'bg-emerald-500 text-white border-emerald-600',
      };
      return `${colorMap[stageValue] || 'bg-primary text-primary-foreground border-primary'} shadow-lg scale-105`;
    }
    return 'bg-muted text-muted-foreground border-border hover:bg-muted/80';
  };

  const getConnectorClasses = (index: number) => {
    const status = getStageStatus(index);
    if (status === 'completed') return 'bg-emerald-500';
    return 'bg-border';
  };

  return (
    <div className="w-full py-2 px-4 bg-gradient-to-br from-card to-card/80 rounded-xl border border-border/50 shadow-sm">
      {/* Header with dates */}
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-border/50">
        {/* Start Date - Editable */}
        <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
          <PopoverTrigger asChild>
            <button className="flex flex-col gap-0.5 hover:bg-muted/50 rounded-lg p-1.5 -m-1.5 transition-colors cursor-pointer text-left group">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                {t('fields.startDate')}
                <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </span>
              {displayStartDate ? (
                <span className="text-xs font-bold text-foreground">{format(parseLocalDate(displayStartDate), 'MMM d, yyyy')}</span>
              ) : (
                <span className="text-xs text-muted-foreground">{t('fields.clickToSet')}</span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={displayStartDate ? parseLocalDate(displayStartDate) : undefined}
              onSelect={(date) => {
                if (date && onStartDateChange) {
                  onStartDateChange(date);
                }
                setStartDateOpen(false);
              }}
              initialFocus
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>

        {/* Closing Date - Editable */}
        <Popover open={closingDateOpen} onOpenChange={setClosingDateOpen}>
          <PopoverTrigger asChild>
            <button className="flex flex-col gap-0.5 text-right hover:bg-muted/50 rounded-lg p-1.5 -m-1.5 transition-colors cursor-pointer group">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-end gap-1">
                <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                {t('fields.closingDate')}
              </span>
              {closedDate ? (
                <span className="text-xs font-bold text-foreground">{format(parseLocalDate(closedDate), 'MMM d, yyyy')}</span>
              ) : expectedCloseDate ? (
                <span className="text-xs font-bold text-amber-600">
                  {format(parseLocalDate(expectedCloseDate), 'MMM d, yyyy')}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">{t('fields.clickToSet')}</span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={displayClosingDate ? parseLocalDate(displayClosingDate) : undefined}
              onSelect={(date) => {
                if (date && onClosingDateChange) {
                  onClosingDateChange(date);
                }
                setClosingDateOpen(false);
              }}
              initialFocus
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Stages progress bar */}
      <div className="relative">
        {/* Background connector line */}
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-border/30" style={{ marginLeft: '10%', marginRight: '10%' }} />
        
        {/* Main stages row + Lost button inline */}
        <div className="relative flex items-start justify-between gap-1">
          {STAGES.map((stage, index) => {
            const status = getStageStatus(index);
            const isClickable = !isClosedLost && canChangeStage;
            
            return (
              <React.Fragment key={stage.value}>
                {/* Stage container */}
                <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0 relative z-10">
                  {/* Stage button/indicator */}
                  <button
                    onClick={() => isClickable && handleStageClick(stage.value)}
                    disabled={!isClickable}
                    className={cn(
                      'relative flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 group',
                      status === 'completed' && (stage.value === 'won' 
                        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 hover:scale-110 hover:shadow-emerald-500/50'
                        : 'bg-primary text-white shadow-lg shadow-primary/30 hover:scale-110 hover:shadow-primary/50'),
                      status === 'current' && (stage.value === 'won' 
                        ? 'bg-emerald-500 text-white shadow-xl shadow-emerald-500/40 scale-110 ring-3 ring-emerald-500/20'
                        : 'bg-primary text-primary-foreground shadow-xl shadow-primary/40 scale-110 ring-3 ring-primary/20'),
                      status === 'future' && 'bg-muted text-muted-foreground hover:bg-muted/80',
                      isClickable && 'cursor-pointer',
                      !isClickable && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {status === 'completed' && (
                      <CheckCircle2 className="w-4 h-4" />
                    )}
                    {status === 'current' && (
                      <Circle className="w-4 h-4 fill-current" />
                    )}
                    {status === 'future' && (
                      <Circle className="w-4 h-4" />
                    )}
                    
                    {isClickable && status === 'future' && (
                      <div className="absolute inset-0 rounded-full border-2 border-primary/0 group-hover:border-primary/50 transition-all duration-300" />
                    )}
                  </button>
                  
                  {/* Stage label */}
                  <span className={cn(
                    "text-[10px] font-semibold leading-tight transition-colors duration-300 text-center",
                    status === 'completed' && (stage.value === 'won' ? 'text-emerald-600' : 'text-primary'),
                    status === 'current' && (stage.value === 'won' ? 'text-emerald-600 font-bold' : 'text-primary font-bold'),
                    status === 'future' && 'text-muted-foreground'
                  )}>
                    {t(stage.labelKey)}
                  </span>
                </div>

                {/* Connector line between stages */}
                {index < STAGES.length - 1 && (
                  <div className="flex items-center justify-center" style={{ marginTop: '16px', width: '40px' }}>
                    <div className={cn(
                      'h-0.5 w-full rounded-full transition-all duration-500',
                      status === 'completed' ? 'bg-primary shadow-sm shadow-primary/30' : 'bg-border/30'
                    )}>
                      {status === 'completed' && (
                        <div className="h-full w-full bg-gradient-to-r from-primary/80 to-primary rounded-full animate-pulse" />
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}

          {/* Lost button inline at the end */}
          <div className="flex flex-col items-center gap-1.5 min-w-0 relative z-10">
            <button
              onClick={() => handleStageClick('lost')}
              disabled={!canChangeStage}
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300',
                isClosedLost
                  ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/30 ring-3 ring-rose-500/20 scale-110'
                  : canChangeStage
                    ? 'bg-muted/50 text-muted-foreground border border-border/50 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 cursor-pointer'
                    : 'bg-muted/30 text-muted-foreground/50 border border-border/30 cursor-not-allowed'
              )}
            >
              <XCircle className="w-4 h-4" />
            </button>
            <span className={cn(
              "text-[10px] font-semibold leading-tight text-center",
              isClosedLost ? 'text-rose-600 font-bold' : 'text-muted-foreground'
            )}>
              {t('stages.closedLost')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
